import api from './api';

interface LoginCredentials {
  username: string;
  password: string;
}

interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_at: string;
}

interface User {
  id: string;
  username?: string;
  email: string;
  full_name?: string;
  is_superuser: boolean;
  avatar_url?: string;
  csrf_token?: string;
  auth_provider?: string;
}

interface AuthConfig {
  local_auth_enabled: boolean;
  oidc_enabled: boolean;
  providers: string[];
  csrf_protection_enabled: boolean;
}

let currentUser: User | null = null;
let authConfig: AuthConfig | null = null;

export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  console.log("[AUTH] Attempting login with username:", credentials.username);
  
  // Use URLSearchParams to format the request body as form data
  const formData = new URLSearchParams();
  formData.append('username', credentials.username);
  formData.append('password', credentials.password);
  
  try {
    console.log("[AUTH] Trying unified login endpoint");
    // Try the new unified login endpoint first
    const response = await api.post('/auth/login/local', formData.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true // Ensure cookies are sent/received
    });
    
    console.log("[AUTH] Login successful:", response.data);
    
    // Store user data
    if (response.data.user) {
      console.log("[AUTH] Storing user data from login");
      currentUser = response.data.user;
    }
    
    // Store CSRF token if provided
    if (response.data.csrf_token) {
      console.log("[AUTH] Setting CSRF token from login");
      api.defaults.headers.common['X-CSRF-Token'] = response.data.csrf_token;
    }
    
    return { 
      access_token: 'session-auth', 
      token_type: 'session', 
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() 
    };
  } catch (error) {
    console.error("[AUTH] Unified login failed, falling back to token-based auth:", error);
    
    // Fall back to token-based auth for backward compatibility
    const response = await api.post('/auth/token', formData.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });
    
    console.log("[AUTH] Token-based login successful");
    
    // Store the token in localStorage
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('token_expires_at', response.data.expires_at);
    
    return response.data;
  }
}

export async function loginWithProvider(provider: string): Promise<void> {
  // Use the full URL with the API base URL
  const baseUrl = api.defaults.baseURL || '/api';
  const loginUrl = `${baseUrl}/auth/login/${provider}`;
  
  console.log(`[AUTH] Redirecting to provider login: ${loginUrl}`);
  window.location.href = loginUrl;
}

export async function logout(): Promise<void> {
  try {
    // Try to use the session-based logout endpoint
    await api.post('/auth/logout');
  } catch (error) {
    console.error('Error during logout:', error);
  }
  
  // Clear local storage regardless
  localStorage.removeItem('token');
  localStorage.removeItem('token_expires_at');
  localStorage.removeItem('currentUser');
  currentUser = null;
  
  // Clear CSRF token
  if (api.defaults.headers.common['X-CSRF-Token']) {
    delete api.defaults.headers.common['X-CSRF-Token'];
  }
}

// Add debouncing for auth checks
let authCheckPromise: Promise<User | null> | null = null;
let authConfigPromise: Promise<AuthConfig> | null = null;

// Add a flag to track if an auth check is in progress
let authCheckInProgress = false;
let authCheckCount = 0;

export async function checkAuthStatus(cacheBuster = ''): Promise<User | null> {
  // Track how many times this function is called
  authCheckCount++;
  console.log(`[AUTH] checkAuthStatus called ${authCheckCount} times`);
  
  // Return existing promise if one is in progress
  if (authCheckPromise) {
    console.log("[AUTH] Using existing auth check promise");
    return authCheckPromise;
  }
  
  // Prevent multiple simultaneous auth checks
  if (authCheckInProgress) {
    console.log("[AUTH] Auth check already in progress, returning null");
    return null;
  }
  
  try {
    console.log("[AUTH] Starting new auth check");
    authCheckInProgress = true;
    
    // Use the configured API URL from environment instead of constructing a new one
    // This ensures we use the same API URL as the rest of the application
    console.log(`[AUTH] Using configured API URL for auth check with cacheBuster: ${cacheBuster}`);
    
    // Create a new promise and store it
    authCheckPromise = api.get(`/auth/me${cacheBuster}`, {
      withCredentials: true, // Ensure cookies are sent
      // Silence 401 errors since they're expected when not logged in
      validateStatus: function (status) {
        return status < 400 || status === 401;
      }
    }).then(response => {
      // If we got a 401, just return null (not authenticated)
      if (response.status === 401) {
        console.log("[AUTH] Not authenticated (401)");
        return null;
      }
      
      console.log("[AUTH] Auth check response:", response.data);
      currentUser = response.data;
      
      // Store CSRF token if provided
      if (response.data.csrf_token) {
        console.log("[AUTH] Setting CSRF token from auth check");
        api.defaults.headers.common['X-CSRF-Token'] = response.data.csrf_token;
        
        // Also store the user data in localStorage for persistence across page reloads
        localStorage.setItem('currentUser', JSON.stringify(currentUser));
      }
      
      return currentUser;
    });
    
    console.log("[AUTH] Awaiting auth check promise result");
    const result = await authCheckPromise;
    console.log("[AUTH] Auth check promise resolved:", result ? "authenticated" : "not authenticated");
    
    // Clear the promise immediately after use
    console.log("[AUTH] Scheduling cleanup of auth check state");
    setTimeout(() => {
      console.log("[AUTH] Cleaning up auth check state");
      authCheckPromise = null;
      authCheckInProgress = false;
    }, 100);
    
    return result;
  } catch (error) {
    console.error("[AUTH] Auth check failed:", error);
    
    // Clear the promise on error
    console.log("[AUTH] Clearing auth check state due to error");
    authCheckPromise = null;
    authCheckInProgress = false;
    
    // If session check fails, check token-based auth
    if (isTokenAuthenticated()) {
      console.log("[AUTH] Using token-based authentication as fallback");
      return { 
        id: 'token-auth', 
        email: 'token-user@example.com', 
        is_superuser: false 
      };
    }
    return null;
  }
}

// Add a flag to track if auth config fetch is in progress
let authConfigInProgress = false;
let authConfigCount = 0;

export async function getAuthConfig(): Promise<AuthConfig> {
  // Track how many times this function is called
  authConfigCount++;
  console.log(`[AUTH] getAuthConfig called ${authConfigCount} times`);
  
  if (authConfig) {
    console.log("[AUTH] Using cached auth config");
    return authConfig;
  }
  
  // Return existing promise if one is in progress
  if (authConfigPromise) {
    console.log("[AUTH] Using existing auth config promise");
    return authConfigPromise;
  }
  
  // Prevent multiple simultaneous auth config fetches
  if (authConfigInProgress) {
    console.log("[AUTH] Auth config fetch already in progress, returning default config");
    return {
      local_auth_enabled: true,
      oidc_enabled: false,
      providers: [],
      csrf_protection_enabled: false
    };
  }
  
  try {
    console.log("[AUTH] Starting new auth config fetch");
    authConfigInProgress = true;
    
    // Use the configured API URL from environment instead of constructing a new one
    // This ensures we use the same API URL as the rest of the application
    console.log(`[AUTH] Using configured API URL for auth config`);
    
    // Create a new promise and store it
    authConfigPromise = api.get('/auth/config', {
      withCredentials: true // Ensure cookies are sent
    }).then(response => {
      console.log("[AUTH] Auth config response:", response.data);
      authConfig = response.data;
      return authConfig;
    });
    
    console.log("[AUTH] Awaiting auth config promise result");
    const result = await authConfigPromise;
    console.log("[AUTH] Auth config promise resolved:", result);
    
    return result;
  } catch (error) {
    console.error("[AUTH] Failed to load auth config:", error);
    // Default config if endpoint not available
    return {
      local_auth_enabled: true,
      oidc_enabled: false,
      providers: [],
      csrf_protection_enabled: false
    };
  } finally {
    // Clear the promise and in-progress flag after a delay
    console.log("[AUTH] Scheduling cleanup of auth config state");
    setTimeout(() => {
      console.log("[AUTH] Cleaning up auth config state");
      authConfigPromise = null;
      authConfigInProgress = false;
    }, 500);
  }
}

export function isTokenAuthenticated(): boolean {
  const token = localStorage.getItem('token');
  const expiresAt = localStorage.getItem('token_expires_at');
  
  if (!token || !expiresAt) {
    return false;
  }
  
  // Check if token is expired
  const expiryDate = new Date(expiresAt);
  const now = new Date();
  
  return now < expiryDate;
}

export function isAuthenticated(): boolean {
  return currentUser !== null || isTokenAuthenticated();
}

export function getToken(): string | null {
  return localStorage.getItem('token');
}

export function getCurrentUser(): User | null {
  return currentUser;
}

export async function preRegisterUser(email: string, provider: string): Promise<any> {
  // Don't call checkAuthStatus here to avoid infinite loops
  const response = await api.post('/auth/pre-register', { email, provider });
  return response.data;
}

export async function getPreRegisteredUsers(): Promise<any[]> {
  // Explicitly remove trailing slash to avoid redirects
  const response = await api.get('/auth/pre-registered-users');
  return response.data;
}
